import React, { useCallback, useEffect, useRef, useState } from 'react';
import { BookOpen, ArrowLeft, ExternalLink, X } from 'lucide-react';
import Button from '../shared/Button';
import supportService from '../../services/supportService';
import { useAuth } from '../../contexts/AuthContext';
import './DocsButton.css';

// THE Docs button — opens the hub's own documentation (Confluence space HHD) for the screen it sits on, live, in
// the hub. A reader needs no Confluence account: the page comes through GET /support/docs/{id}, which reads it
// with the hub's Atlassian token, refuses anything outside the documentation space and returns sanitised HTML.
//
//   pageId   the Confluence page id (a sync template's docs_page_id …). No id, no button.
//   label    button text (default "Docs"); `variant`, `className` pass through to the button.
//
// Links between documentation pages (data-doc-page-id, set by the server) open in the viewer, with Back; any other
// link opens in a new tab. A superadmin also gets "Open in Confluence", to edit the page.

const errText = (e) => e.response?.data?.error?.message || e.response?.data?.message || e.message;
const when = (v) => (v ? new Date(v).toLocaleDateString() : null);

export function DocsViewer({ pageId, onClose }) {
    const { isSuperAdmin } = useAuth();
    const [trail, setTrail] = useState([pageId]);         // pages opened, the current one last
    const [page, setPage] = useState(null);
    const [error, setError] = useState(null);
    const bodyRef = useRef(null);
    const current = trail[trail.length - 1];

    useEffect(() => {
        let live = true;
        setPage(null);
        setError(null);
        supportService.getDoc(current)
            .then(r => { if (live) setPage(r.data); })
            .catch(e => { if (live) setError(e.response?.status === 404 ? 'This documentation page is not available.' : errText(e)); });
        return () => { live = false; };
    }, [current]);

    useEffect(() => { if (bodyRef.current) bodyRef.current.scrollTop = 0; }, [page]);

    const open = useCallback((id) => { if (id && id !== current) setTrail(t => [...t, id]); }, [current]);

    // One listener on the body: a documentation link opens in place, nothing else is intercepted.
    const onBodyClick = (e) => {
        const a = e.target.closest && e.target.closest('a[data-doc-page-id]');
        if (!a) return;
        e.preventDefault();
        open(a.getAttribute('data-doc-page-id'));
    };

    const linksInBody = page && /data-doc-page-id=/.test(page.html || '');
    const empty = page && !String(page.html || '').replace(/<[^>]*>/g, '').trim();

    return (
        <div className="docs-overlay" onClick={onClose}>
            <div className="docs-modal" onClick={e => e.stopPropagation()} role="dialog" aria-label="Documentation">
                <div className="docs-head">
                    {trail.length > 1 && (
                        <button className="docs-icon-btn" onClick={() => setTrail(t => t.slice(0, -1))} title="Back">
                            <ArrowLeft size={16} />
                        </button>
                    )}
                    <div className="docs-title">{page ? page.title : 'Documentation'}</div>
                    {page && isSuperAdmin && page.url && (
                        <a className="docs-icon-btn" href={page.url} target="_blank" rel="noopener noreferrer" title="Open in Confluence (to edit)">
                            <ExternalLink size={16} />
                        </a>
                    )}
                    <button className="docs-icon-btn" onClick={onClose} title="Close"><X size={16} /></button>
                </div>
                <div className="docs-body" ref={bodyRef}>
                    {error && <div className="docs-msg docs-msg-error">{error}</div>}
                    {!page && !error && <div className="docs-msg">Loading…</div>}
                    {page && (
                        <>
                            {/* Sanitised on the server (/opt/utils/confluence, sanitize-html allow-list). */}
                            {!empty && <div className="docs-content" onClick={onBodyClick} dangerouslySetInnerHTML={{ __html: page.html }} />}
                            {empty && !page.children.length && <div className="docs-msg">This page has not been written yet.</div>}
                            {/* The page's sub-pages, when its own text does not already link to them. */}
                            {!linksInBody && page.children.length > 0 && (
                                <div className="docs-children">
                                    <div className="docs-children-title">In this section</div>
                                    <ul>
                                        {page.children.map(c => (
                                            <li key={c.id}><button type="button" className="docs-link" onClick={() => open(c.id)}>{c.title}</button></li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            {page.updatedAt && <div className="docs-meta">Last updated {when(page.updatedAt)}</div>}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export default function DocsButton({ pageId, label = 'Docs', variant = 'secondary', className }) {
    const [open, setOpen] = useState(false);
    if (!pageId) return null;
    return (
        <>
            <Button variant={variant} className={className} onClick={() => setOpen(true)}>
                <BookOpen className="icon" /> {label}
            </Button>
            {open && <DocsViewer pageId={String(pageId)} onClose={() => setOpen(false)} />}
        </>
    );
}
